import { useMemo } from 'react'
import * as THREE from 'three'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { FaiscasChoqueEletrico3D } from '../componentes/FogoEFaiscasChoque3D'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { paraArray, vetor, type Vetor3 } from '../../dominio/geometria/vetor3'
import { alvoImpacto, type TipoAlvoImpacto } from '../../dominio/didatico/alvosImpacto'
import { amostrarTrajetoria, type TrajetoriaCorpo } from '../../fisica/trajetoria'
import {
  amostrarCinematica,
  instanteDoContato,
  posicaoDoVeiculo,
  type TipoColisao,
} from '../../dominio/didatico/timelineCinematica'
import { VeiculoAlvoCompleto } from '../veiculo/VeiculoDidatico'
import {
  CLASSES_VEICULO,
  amostrarPoseOutroVeiculo,
  type ColisaoEntreVeiculos,
} from '../../dominio/didatico/colisaoEntreVeiculos'
import type { EstadoDeformacaoVeiculo } from '../../dominio/didatico/campoDeformacao'
import { DeformacaoImpacto3D } from './DeformacaoImpacto3D'

interface Props {
  tipo: TipoAlvoImpacto
  direcaoImpacto: Vetor3 | null
  perfil: PerfilVeiculo
  tempo: number
  trajetoriaFisica?: TrajetoriaCorpo | null
  tipoColisao?: TipoColisao
  velocidadeImpacto?: number
  /** instante do contato deste alvo; por padrão, o primeiro contato do tipo.
   * Permite renderizar um segundo alvo (o segundo contato do engavetamento)
   * com seu próprio instante. */
  instanteContato?: number
  /**
   * Colisão veículo × veículo resolvida pelo domínio. Quando presente, o
   * outro veículo usa o modelo determinístico (aproximação → compressão →
   * separação → repouso), com classe, perfil e deformação próprios.
   */
  colisaoVeiculos?: ColisaoEntreVeiculos | null
  /** deformação do OUTRO veículo neste instante (nunca a do veículo da maquete) */
  deformacaoOutro?: EstadoDeformacaoVeiculo | null
  /** centro do contato na face atingida do veículo da maquete: x (frente/traseira) ou z (lateral) */
  centroContato?: number
  /** small overlap contra muro: a barreira cobre só o canto */
  larguraContato?: number
  smallOverlap?: boolean
  mostrarMapaDeformacao?: boolean
}

/**
 * Alvo externo na cinemática com sincronização estrita de contato:
 * - No instante exato de contato (t = instanteDoContato), as superfícies se tocam com distância 0.
 * - Antes do contato, há aproximação suave e contínua a partir de distância de trânsito.
 * - Durante a desaceleração, o carro-alvo/obstáculo permanece acoplado à zona de deformação.
 * - O outro veículo é renderizado como modelo veicular completo com acabamento automotivo.
 */
export function AlvoImpacto3D({
  tipo,
  direcaoImpacto,
  perfil,
  tempo,
  trajetoriaFisica,
  tipoColisao = 'frontal',
  velocidadeImpacto,
  instanteContato,
  colisaoVeiculos,
  deformacaoOutro,
  centroContato,
  larguraContato,
  smallOverlap = false,
  mostrarMapaDeformacao = false,
}: Props) {
  if (!direcaoImpacto) return null

  // 0. Outro veículo com modelo didático próprio (preferido quando resolvido)
  if (tipo === 'outroVeiculo' && colisaoVeiculos) {
    const pose = amostrarPoseOutroVeiculo(colisaoVeiculos, tempo)
    const inicial = amostrarPoseOutroVeiculo(colisaoVeiculos, 0)
    const distanciaRodada = ((pose.posicao.x - inicial.posicao.x) * Math.sin(inicial.guinadaRad)
      + (pose.posicao.z - inicial.posicao.z) * Math.cos(inicial.guinadaRad)) / colisaoVeiculos.escalaB[2]
    const classe = CLASSES_VEICULO[colisaoVeiculos.classeB]
    const perfilB = colisaoVeiculos.perfilB
    return (
      <group position={paraArray(pose.posicao)} rotation={[0, pose.guinadaRad, 0]}>
        <group scale={colisaoVeiculos.escalaB}>
          <VeiculoAlvoCompleto perfil={perfilB} deformacao={deformacaoOutro} distanciaRodada={distanciaRodada} />
          {mostrarMapaDeformacao && deformacaoOutro && (
            <DeformacaoImpacto3D perfil={perfilB} estado={deformacaoOutro} rotulo="B" />
          )}
        </group>
        <Rotulo3D
          posicao={[0, perfilB.tetoY * classe.escala[1] + 0.45, 0]}
          texto={`B • ${classe.nome.toLowerCase()} • ${Math.round(colisaoVeiculos.velocidadeBKmh)} km/h`}
          variante="risco"
          fator={5}
        />
      </group>
    )
  }

  const longitudinal = Math.abs(direcaoImpacto.z) >= Math.abs(direcaoImpacto.x)
  const ladoFrente = direcaoImpacto.z < 0
  // a força aponta para +X ⇒ o contato é na face −X (passageiro), e vice-versa
    const contatoEmMenosX = direcaoImpacto.x > 0

  const tContato = instanteContato ?? instanteDoContato(tipoColisao)
  const quadroContato = amostrarCinematica(tipoColisao, tContato, velocidadeImpacto)
  const posContato = posicaoDoVeiculo(perfil, quadroContato)

  const quadroAtual = amostrarCinematica(tipoColisao, tempo, velocidadeImpacto)
  const posAtual = posicaoDoVeiculo(perfil, quadroAtual)

  // Face de contato do veículo principal no momento exato do impacto (t = tContato)
  const zContatoFace = longitudinal
    ? posContato.z + (ladoFrente ? perfil.frenteZ : perfil.traseiraZ)
    : posContato.z
  const xContatoFace = !longitudinal
    ? posContato.x + (contatoEmMenosX ? -perfil.largura / 2 : perfil.largura / 2)
    : posContato.x

  // Face atual do veículo principal em função do tempo
  const zAtualFace = longitudinal
    ? posAtual.z + (ladoFrente ? perfil.frenteZ : perfil.traseiraZ)
    : posAtual.z
  const xAtualFace = !longitudinal
    ? posAtual.x + (contatoEmMenosX ? -perfil.largura / 2 : perfil.largura / 2)
    : posAtual.x

  // 1. Alvo: Poste (objeto fixo com detalhamento estrutural e faixas de advertência)
  if (tipo === 'poste') {
    const raioPoste = 0.18
    // o poste fica no ponto de contato (small overlap: no canto; lateral: na porta escolhida)
    const posPoste: [number, number, number] = longitudinal
      ? [posContato.x + (centroContato ?? 0), 0, ladoFrente ? zContatoFace + raioPoste : zContatoFace - raioPoste]
      : [
          contatoEmMenosX ? xContatoFace - raioPoste : xContatoFace + raioPoste,
          0,
          posContato.z + (centroContato ?? perfil.ancoras[0]?.posicao.z ?? 0),
        ]

    return (
      <group position={posPoste}>
        <PosteVisual />
        <Rotulo3D posicao={[0, 2.45, 0]} texto="poste • objeto fixo" variante="risco" fator={5} />
      </group>
    )
  }

  // 2. Alvo: Muro (barreira de concreto New Jersey estrutural)
  if (tipo === 'muro') {
    const meiaEspessura = 0.16
    // small overlap: a borda da barreira coincide com a borda interna da sobreposição
    const xMuro =
      longitudinal && smallOverlap && centroContato != null && larguraContato != null
        ? posContato.x + centroContato - Math.sign(centroContato || 1) * (larguraContato / 2) + Math.sign(centroContato || 1) * 3.1
        : 0
    const posMuro: [number, number, number] = longitudinal
      ? [xMuro, 0, ladoFrente ? zContatoFace + meiaEspessura : zContatoFace - meiaEspessura]
      : [
          contatoEmMenosX ? xContatoFace - meiaEspessura : xContatoFace + meiaEspessura,
          0,
          posContato.z + (centroContato ?? perfil.ancoras[0]?.posicao.z ?? 0),
        ]

    return (
      <group position={posMuro}>
        <MuroVisual longitudinal={longitudinal} />
        <Rotulo3D posicao={[0, 2.65, 0]} texto="muro • barreira rígida" variante="risco" fator={5} />
      </group>
    )
  }

  // 3. Alvo: Outro Veículo com Trajetória Física (Rapier 3D)
  if (tipo === 'outroVeiculo' && trajetoriaFisica) {
    const quadroFisico = amostrarTrajetoria(trajetoriaFisica, tempo)
    const inicial = amostrarTrajetoria(trajetoriaFisica, 0)
    const deslocamento = new THREE.Vector3(quadroFisico.posicao.x - inicial.posicao.x, quadroFisico.posicao.y - inicial.posicao.y, quadroFisico.posicao.z - inicial.posicao.z)
      .applyQuaternion(new THREE.Quaternion(inicial.rotacao.x, inicial.rotacao.y, inicial.rotacao.z, inicial.rotacao.w).invert())
    return (
      <group
        position={paraArray(quadroFisico.posicao)}
        quaternion={[quadroFisico.rotacao.x, quadroFisico.rotacao.y, quadroFisico.rotacao.z, quadroFisico.rotacao.w]}
      >
        <VeiculoAlvoCompleto perfil={perfil} distanciaRodada={deslocamento.z} />
        <Rotulo3D
          posicao={[0, perfil.tetoY + 0.45, 0]}
          texto="outro veículo • resposta física"
          variante="risco"
          fator={5}
        />
      </group>
    )
  }

  // 4. Alvo: Outro Veículo Procedural com Sincronização Estrita
  const distanciaAproximacao = 7.0
  const progressoAprox = Math.min(1, Math.max(0, tempo / tContato))

  let centroX = 0
  let centroZ = 0
  let rotacaoY = 0

  if (longitudinal) {
    if (ladoFrente) {
      // Colisão Frontal: outro veículo vem pela frente (+Z) virado para trás (rotacaoY = PI)
      rotacaoY = Math.PI
      const centroContatoZ = zContatoFace + perfil.frenteZ
      if (tempo < tContato) {
        centroZ = centroContatoZ + distanciaAproximacao * (1 - progressoAprox)
      } else {
        // Acoplamento estrito: o para-choque do carro-alvo acompanha o para-choque do veículo
        centroZ = zAtualFace + perfil.frenteZ
      }
      centroX = 0
    } else {
      // Colisão Traseira: outro veículo vem por trás (-Z) virado para frente (rotacaoY = 0)
      rotacaoY = 0
      const centroContatoZ = zContatoFace - perfil.frenteZ
      if (tempo < tContato) {
        centroZ = centroContatoZ - distanciaAproximacao * (1 - progressoAprox)
      } else {
        centroZ = zAtualFace - perfil.frenteZ
      }
      centroX = 0
    }
  } else {
    // Colisão Lateral
    const zAlvo = perfil.ancoras[0]?.posicao.z ?? 0
    centroZ = zAlvo
    if (contatoEmMenosX) {
      // Contato na face −X (passageiro): vem de −X virado para +X (rotacaoY = PI/2)
      rotacaoY = Math.PI / 2
      const centroContatoX = xContatoFace - perfil.frenteZ
      if (tempo < tContato) {
        centroX = centroContatoX - distanciaAproximacao * (1 - progressoAprox)
      } else {
        centroX = xAtualFace - perfil.frenteZ
      }
    } else {
      // Contato na face +X (motorista): vem de +X virado para −X (rotacaoY = −PI/2)
      rotacaoY = -Math.PI / 2
      const centroContatoX = xContatoFace + perfil.frenteZ
      if (tempo < tContato) {
        centroX = centroContatoX + distanciaAproximacao * (1 - progressoAprox)
      } else {
        centroX = xAtualFace + perfil.frenteZ
      }
    }
  }

  return (
    <group position={[centroX, 0, centroZ]} rotation={[0, rotacaoY, 0]}>
      <VeiculoAlvoCompleto perfil={perfil} distanciaRodada={centroX * Math.sin(rotacaoY) + centroZ * Math.cos(rotacaoY)} />
      <Rotulo3D
        posicao={[0, perfil.tetoY + 0.45, 0]}
        texto={`${alvoImpacto(tipo).nome.toLowerCase()} • em movimento`}
        variante="risco"
        fator={5}
      />
    </group>
  )
}

/**
 * Modelo visual aprimorado do Poste: flange de fixação na base, parafusos,
 * fuste metálico/concreto e faixas zebradas de advertência reflexivas.
 */
function PosteVisual() {
  const curvaFio = useMemo(
    () =>
      new THREE.CatmullRomCurve3([
        new THREE.Vector3(0.55, 3.28, 0),
        new THREE.Vector3(0.68, 2.3, 0.14),
        new THREE.Vector3(0.78, 1.2, 0.32),
        new THREE.Vector3(0.88, 0.04, 0.48),
      ]),
    [],
  )

  return (
    <group>
      {/* Flange / sapata de fixação de aço na base */}
      <mesh position={[0, 0.02, 0]}>
        <boxGeometry args={[0.52, 0.04, 0.52]} />
        <meshStandardMaterial color="#334155" roughness={0.7} metalness={0.6} />
      </mesh>

      {/* 4 Parafusos de ancoragem nos cantos da flange */}
      {[-0.2, 0.2].flatMap((x) =>
        [-0.2, 0.2].map((z) => (
          <mesh key={`${x}:${z}`} position={[x, 0.05, z]}>
            <cylinderGeometry args={[0.02, 0.02, 0.04, 8]} />
            <meshStandardMaterial color="#64748b" roughness={0.3} metalness={0.9} />
          </mesh>
        )),
      )}

      {/* Fuste cilíndrico principal do poste */}
      <mesh position={[0, 1.6, 0]} castShadow>
        <cylinderGeometry args={[0.16, 0.2, 3.2, 20]} />
        <meshStandardMaterial color="#475569" roughness={0.85} metalness={0.25} />
      </mesh>

      {/* Faixas zebradas de advertência amarela e preta em anel reflexivo */}
      {[0.95, 1.15, 1.35].map((y, i) => (
        <mesh key={y} position={[0, y, 0]}>
          <cylinderGeometry args={[0.185, 0.185, 0.12, 20]} />
          <meshStandardMaterial
            color={i % 2 === 0 ? '#eab308' : '#0f172a'}
            emissive={i % 2 === 0 ? '#ca8a04' : '#000000'}
            emissiveIntensity={i % 2 === 0 ? 0.35 : 0}
            roughness={0.65}
          />
        </mesh>
      ))}

      {/* Cruzeta superior de distribuição de energia */}
      <mesh position={[0, 3.22, 0]}>
        <boxGeometry args={[1.4, 0.12, 0.12]} />
        <meshStandardMaterial color="#1e293b" roughness={0.7} metalness={0.5} />
      </mesh>

      {/* Isoladores nos terminais da cruzeta */}
      {[-0.55, 0, 0.55].map((x, idx) => (
        <mesh key={idx} position={[x, 3.32, 0]}>
          <cylinderGeometry args={[0.03, 0.05, 0.1, 8]} />
          <meshStandardMaterial color="#78350f" roughness={0.3} metalness={0.1} />
        </mesh>
      ))}

      {/* Fio de alta tensão partido e caído até o chão */}
      <mesh>
        <tubeGeometry args={[curvaFio, 24, 0.016, 6, false]} />
        <meshStandardMaterial color="#1e293b" roughness={0.7} />
      </mesh>

      {/* Faíscas elétricas de choque saindo do fio partido no topo */}
      <FaiscasChoqueEletrico3D posicao={[0.55, 3.28, 0]} escala={0.7} ativo={true} />

      {/* Faíscas elétricas e arcos de choque no ponto de contato no solo */}
      <FaiscasChoqueEletrico3D posicao={[0.88, 0, 0.48]} escala={1.1} ativo={true} />
    </group>
  )
}

/**
 * Modelo visual da Barreira / Muro de Concreto tipo New Jersey com base chanfrada
 * e textura de concreto armado de alta resistência.
 */
function MuroVisual({ longitudinal }: { longitudinal: boolean }) {
  const comp = 6.2
  const alt = 2.4
  const esp = 0.32

  const dimensoes: [number, number, number] = longitudinal ? [comp, alt, esp] : [esp, alt, comp]

  return (
    <group>
      {/* Bloco principal do muro/barreira de concreto */}
      <mesh position={[0, alt / 2, 0]} receiveShadow>
        <boxGeometry args={dimensoes} />
        <meshStandardMaterial color="#64748b" roughness={0.96} metalness={0.08} />
      </mesh>

      {/* Base alargada de sapata anti-tombamento estilo New Jersey */}
      <mesh position={[0, 0.22, 0]}>
        <boxGeometry
          args={
            longitudinal
              ? [comp + 0.1, 0.44, esp + 0.22]
              : [esp + 0.22, 0.44, comp + 0.1]
          }
        />
        <meshStandardMaterial color="#475569" roughness={0.98} metalness={0.05} />
      </mesh>

      {/* Faixa reflexiva de tráfego superior */}
      <mesh position={[0, alt - 0.2, 0]}>
        <boxGeometry
          args={
            longitudinal
              ? [comp + 0.01, 0.15, esp + 0.01]
              : [esp + 0.01, 0.15, comp + 0.01]
          }
        />
        <meshStandardMaterial color="#cbd5e1" roughness={0.5} metalness={0.3} />
      </mesh>
    </group>
  )
}

